import { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { ROUTES } from '@/config/routes'
import {
  useCurrentTeamId,
  useIsAuthenticated,
  useValidateTeamContext,
  useHasHydrated,
} from '@/stores/auth'

interface TeamContextGuardProps {
  children: React.ReactNode
}

/**
 * TeamContextGuard - Route guard that ensures user has selected a team
 *
 * Flow:
 * 1. Wait for Zustand store hydration (prevents race conditions during HMR)
 * 2. If user is not authenticated → ProtectedRoute handles redirect to login
 * 3. If user has no team context → redirect to team selection (Netflix/Prime style)
 * 4. If user has team context → validate it and render children
 *
 * Note: We do NOT auto-select teams. User must always explicitly choose
 * a team from the Team Selection page (like Netflix profile selection)
 */
export function TeamContextGuard({ children }: TeamContextGuardProps) {
  const location = useLocation()
  const hasHydrated = useHasHydrated()
  const isAuthenticated = useIsAuthenticated()
  const currentTeamId = useCurrentTeamId()
  const validateTeamContext = useValidateTeamContext()

  const [isValidating, setIsValidating] = useState(true)
  const [isValid, setIsValid] = useState(false)

  useEffect(() => {
    const validateContext = async () => {
      // Wait for Zustand to hydrate from localStorage
      // This prevents race conditions during HMR where we read initial (empty) state
      if (!hasHydrated) {
        return // Don't make decisions until hydration is complete
      }

      // Not authenticated - don't process further
      if (!isAuthenticated) {
        setIsValidating(false)
        return
      }

      // No team selected - redirect to team selection
      if (!currentTeamId) {
        setIsValid(false)
        setIsValidating(false)
        return
      }

      // Validate the current team context
      try {
        const valid = await validateTeamContext()
        setIsValid(valid)
      } catch (error) {
        console.error('Team context validation failed:', error)
        setIsValid(false)
      }

      setIsValidating(false)
    }

    validateContext()
  }, [hasHydrated, isAuthenticated, currentTeamId, validateTeamContext])

  // Show loading while waiting for hydration or validating
  if (!hasHydrated || isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">
            {!hasHydrated ? 'Loading...' : 'Validating team context...'}
          </p>
        </div>
      </div>
    )
  }

  // If no team context or invalid, redirect to team selection
  if (!currentTeamId || !isValid) {
    return (
      <Navigate
        to={ROUTES.SELECT_TEAM}
        state={{ from: location }}
        replace
      />
    )
  }

  // Team context is valid - render children
  return <>{children}</>
}

export default TeamContextGuard
